import { useEffect, useRef } from 'react';
import { getTranslations } from '../utils/translations';
import { stripHtml } from '../utils/htmlUtils';

const EPISODE_LOGO_URL = 'https://pawsandtales.org/wp-content/uploads/2024/06/episode-logo.png';
const JOIN_CLUB_PATH = '/join-the-club/';

// Matches the theme's solid buttons on /tools-for-parents/. (The playlist rows'
// "Parent Tools" trigger is deliberately the quiet outline version of this pill.)
const pillStyle = {
    display: 'inline-block',
    backgroundColor: '#E0660D',
    color: '#fff',
    border: '3px solid #fff',
    borderRadius: '40px',
    padding: '9px 22px 7px',
    fontFamily: '"Burbank Regular", sans-serif',
    fontWeight: 200,
    fontSize: '16px',
    lineHeight: '27px',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3)',
};

/**
 * Bottom-sheet modal opened by the playlist rows' "Parent Tools" pill
 * (Paws & Tales only). Design: docs/design/tools-for-parents-modal-sketch.html.
 *
 * Member vs non-member is decided server-side: the theme emits ifl:parentToolPDF /
 * ifl:activitySheetPDF in SeriesJSON only for club members, so this component simply
 * renders PDF pills when the fields are present and the join CTA when they are not.
 * Playback intentionally continues underneath.
 */
function ParentToolsModal({ item, episodeNumber, onClose }) {
    const t = getTranslations();
    const sheetRef = useRef(null);
    const closingViaPopRef = useRef(false);
    const touchStartYRef = useRef(null);

    const hasParentGuide = !!item?.parentToolPdf;
    const hasActivitySheet = !!item?.activitySheetPdf;
    const isMemberView = hasParentGuide || hasActivitySheet;

    // Android/browser back closes the sheet instead of leaving the page.
    useEffect(() => {
        window.history.pushState({ pntParentTools: true }, '');
        const onPopState = () => {
            closingViaPopRef.current = true;
            onClose();
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            // Closed via UI (backdrop/✕/Escape/swipe): consume the state we pushed.
            if (!closingViaPopRef.current) {
                window.history.back();
            }
        };
    }, [onClose]);

    // Lock page scroll, drop the control bar's max z-index (App.css), and move
    // focus into the dialog while open.
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('pnt-parent-tools-open');
        sheetRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.classList.remove('pnt-parent-tools-open');
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    if (!item) return null;

    const synopsis = stripHtml(item.messageSummary || '');
    const eyebrow = episodeNumber ? `${episodeNumber}. ${stripHtml(item.name || '')}` : stripHtml(item.name || '');

    const onTouchStart = (e) => { touchStartYRef.current = e.touches[0].clientY; };
    const onTouchMove = (e) => {
        if (touchStartYRef.current !== null && e.touches[0].clientY - touchStartYRef.current > 80) {
            touchStartYRef.current = null;
            onClose();
        }
    };

    const logo = (
        <img
            src={EPISODE_LOGO_URL}
            alt=""
            style={{ width: '150px', margin: '2px 0' }}
        />
    );

    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 100000 }}
        >
            <div
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-label={t.inThisEpisode}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: '#143c1d',
                    borderRadius: '22px 22px 0 0',
                    padding: '10px 20px 28px',
                    boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.45)',
                    color: '#fffdf5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    maxHeight: '85vh',
                    overflowY: 'auto',
                    outline: 'none',
                    textAlign: 'center',
                }}
            >
                <div style={{ width: '44px', height: '5px', borderRadius: '3px', backgroundColor: 'rgba(255, 255, 255, 0.4)', flex: 'none' }} />
                <button
                    onClick={onClose}
                    aria-label={t.close}
                    style={{
                        position: 'absolute', top: '12px', right: '16px',
                        width: '28px', height: '28px', borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)', color: '#fff',
                        border: 'none', fontSize: '15px', lineHeight: '28px', cursor: 'pointer', padding: 0,
                    }}
                >
                    ✕
                </button>
                <div style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#ffbd80', marginTop: '2px' }}>
                    {eyebrow}
                </div>
                <h2 style={{ margin: 0, color: '#fff', fontSize: '30px', textShadow: '2px 2px 0 rgba(0, 0, 0, 0.45)' }}>
                    {t.inThisEpisode}
                </h2>
                {synopsis && (
                    <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.55, color: '#eef3e6' }}>{synopsis}</p>
                )}
                {isMemberView ? (
                    <>
                        {hasParentGuide && (
                            <a href={item.parentToolPdf} target="_blank" rel="noreferrer" style={pillStyle}>{t.parentGuidePdf}</a>
                        )}
                        {logo}
                        {hasActivitySheet && (
                            <a href={item.activitySheetPdf} target="_blank" rel="noreferrer" style={pillStyle}>{t.activitySheetPdf}</a>
                        )}
                    </>
                ) : (
                    <>
                        {logo}
                        <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.5, color: '#eef3e6', maxWidth: '34ch' }}>{t.joinClubCopy}</p>
                        <a href={JOIN_CLUB_PATH} style={pillStyle}>{t.joinClub}</a>
                    </>
                )}
            </div>
        </div>
    );
}

export default ParentToolsModal;
